import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import AxiosClient from '../../assets/AxiosClient';
import { useStateContext } from '../../Contexts/ContextProvider';

const SessionDetail = () => {
  const { id } = useParams();
  const { SetNotification, API } = useStateContext();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCount, setSavingCount] = useState(false);
  const [editingCount, setEditingCount] = useState(false);
  const [quizToClear, setQuizToClear] = useState(null);
  const [clearPhrase, setClearPhrase] = useState('');
  const [clearingResponses, setClearingResponses] = useState(false);
  const actualCountRef = useRef(null);

  const fetchDetail = async () => {
    try {
      const response = await AxiosClient.get(`/sessions/${id}`);
      setSession(response.data);
    } catch (error) {
      SetNotification('Error loading session', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSaveCount = async () => {
    setSavingCount(true);
    const val = actualCountRef.current.value;
    const payload = { actual_count: val === '' ? null : parseInt(val, 10) };
    try {
      const response = await AxiosClient.put(`/sessions/${id}`, payload);
      if (response.status === 200) {
        setSession(response.data.session);
        setEditingCount(false);
        SetNotification('Actual count updated', 'success');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Error updating count';
      SetNotification(msg, 'danger');
    }
    setSavingCount(false);
  };

  const toggleQuizPublish = async (quiz) => {
    const endpoint = quiz.is_published ? null : 'publish';
    if (!endpoint) {
      SetNotification('Use close to stop a published quiz', 'info');
      return;
    }
    try {
      await AxiosClient.post(`/sessions/${id}/quizzes/${quiz.id}/${endpoint}`);
      SetNotification(`Quiz ${endpoint}ed`, 'success');
      fetchDetail();
    } catch (error) {
      SetNotification('Error updating quiz', 'danger');
    }
  };

  const closeQuiz = async (quiz) => {
    try {
      await AxiosClient.post(`/sessions/${id}/quizzes/${quiz.id}/close`);
      SetNotification('Quiz closed', 'success');
      fetchDetail();
    } catch (error) {
      SetNotification('Error closing quiz', 'danger');
    }
  };

  const clearQuizResponses = async () => {
    if (!quizToClear || clearPhrase !== 'CLEAR') return;
    setClearingResponses(true);
    try {
      await AxiosClient.post(`/sessions/${id}/quizzes/${quizToClear.id}/clear-responses`);
      setQuizToClear(null);
      setClearPhrase('');
      SetNotification('Quiz responses cleared', 'success');
      fetchDetail();
    } catch (error) {
      SetNotification('Error clearing quiz responses', 'danger');
    } finally {
      setClearingResponses(false);
    }
  };

  const reopenQuiz = async (quiz) => {
    try {
      await AxiosClient.post(`/sessions/${id}/quizzes/${quiz.id}/reopen`);
      SetNotification('Quiz reopened', 'success');
      fetchDetail();
    } catch (error) {
      SetNotification('Error reopening quiz', 'danger');
    }
  };

  const deleteQuiz = async (qid) => {
    if (!window.confirm('Delete this quiz?')) return;
    try {
      await AxiosClient.delete(`/sessions/${id}/quizzes/${qid}`);
      SetNotification('Quiz deleted', 'success');
      fetchDetail();
    } catch (error) {
      SetNotification('Error deleting quiz', 'danger');
    }
  };

  const examStatus = session?.exam_status || 'closed';
  const examCurrentIndex = Number(session?.exam_current_question_index || 0);

  const triggerExamAction = async (action, successText) => {
    try {
      await AxiosClient.post(`/sessions/${id}/exams/${action}`);
      SetNotification(successText, 'success');
      fetchDetail();
    } catch (error) {
      SetNotification(error.response?.data?.message || `Error while ${action}ing exam`, 'danger');
    }
  };

  const qrSrc = (target) => `${API}/qr?text=${encodeURIComponent(target)}`;

  const downloadQr = async (target, filename) => {
    try {
      const res = await AxiosClient.get(qrSrc(target), { responseType: 'blob' });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      SetNotification('Error downloading QR', 'danger');
    }
  };

  if (loading) return <h6 className="m-3">loading ..</h6>;
  if (!session) return <h6 className="m-3">Session not found</h6>;

  return (
    <div className="m-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>{session.title}</h3>
        <Link to="./quiz/Add" className="btn btn-dark">+ Add Quiz</Link>
      </div>
      <p className="text-muted">
        Start: {session.start_time || '-'} | End: {session.end_time || '-'} |
        Active: {session.is_active ? 'Yes' : 'No'}
      </p>
      {session.description && <p>{session.description}</p>}

      <div className="card p-3 mb-3" style={{ width: '240px' }}>
        <h6>Q&amp;A Page QR</h6>
        <img
          src={qrSrc(`https://ask.velorates.org/${id}`)}
          alt="Session Q&A QR"
          className="img-fluid"
        />
        <button
          className="btn btn-sm btn-outline-secondary mt-2 w-100"
          onClick={() =>
            downloadQr(`https://ask.velorates.org/${id}`, `session-${id}-qa-qr.png`)
          }
        >
          Download
        </button>
      </div>

      <div className="card p-3 mb-3" style={{ width: '280px' }}>
        <h6>Exam QR</h6>
        <img
          src={qrSrc(`${API}/exams/${id}`)}
          alt="Session exam QR"
          className="img-fluid"
        />
        <div className="d-grid gap-2 mt-2">
          <a
            href={`${API}/exams/${id}`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-primary"
          >
            Open public exam
          </a>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => downloadQr(`${API}/exams/${id}`, `session-${id}-exam-qr.png`)}
          >
            Download QR
          </button>
        </div>
      </div>

      <hr />

      <div className="card p-4 mb-3 border-0" style={{ background: 'linear-gradient(135deg, rgba(79,78,214,0.95), rgba(35,117,214,0.95))', color: '#fff', boxShadow: '0 18px 44px rgba(55, 58, 124, 0.22)' }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h4 className="mb-0 text-white">Exam Board</h4>
          <span className={`badge ${examStatus === 'closed' ? 'bg-secondary' : examStatus === 'finished' ? 'bg-primary' : 'bg-success'}`} style={{ background: examStatus === 'closed' ? 'rgba(255,255,255,0.15)' : examStatus === 'finished' ? 'rgba(34,195,166,0.25)' : 'rgba(255,177,59,0.28)', color: '#fff' }}>
            {examStatus === 'closed' ? 'Closed' : examStatus === 'finished' ? 'Finished' : 'Open'}
          </span>
        </div>
        <p className="mb-3 text-white-50">
          Host controls for the live Kahoot-style exam.
        </p>
        <div className="d-flex gap-2 flex-wrap mb-3">
          <button type="button" className="btn btn-warning btn-sm px-3 py-2 fw-bold" onClick={() => triggerExamAction('open', 'Exam opened')} style={{ minWidth: 120 }}>Open exam</button>
          <button type="button" className="btn btn-light btn-sm px-3 py-2 fw-bold" onClick={() => triggerExamAction('next', 'Next question opened')} style={{ minWidth: 140, color: '#2b2a53' }}>Next question</button>
          <button type="button" className="btn btn-outline-light btn-sm px-3 py-2 fw-bold" onClick={() => triggerExamAction('close', 'Exam closed')} style={{ minWidth: 120 }}>Close exam</button>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Link to={`./exam`} className="btn btn-dark btn-sm px-3 py-2 fw-bold">Manage exam</Link>
          <Link to={`./exam/results`} className="btn btn-outline-light btn-sm px-3 py-2 fw-bold">Live results</Link>
        </div>
        <small className="d-block mt-3 text-white-50">
          Current question: {examCurrentIndex + 1}
        </small>
      </div>

      <hr />

      <div className="card p-3 mb-3">
        <h4>Q&amp;A Questions</h4>
        <p className="mb-2">
          {session.questions && session.questions.length > 0
            ? `${session.questions.length} question(s) submitted.`
            : 'No questions yet.'}
        </p>
        <Link to={`./questions`} className="btn btn-dark">
          Manage Questions
        </Link>
      </div>

      <hr />

      <h4>Live Quizzes</h4>
      {!session.quizzes || session.quizzes.length === 0 ? (
        <p className="text-muted">No quizzes yet.</p>
      ) : (
        session.quizzes.map((quiz) => (
          <div key={quiz.id} className="card mb-2 p-3">
            <div className="d-flex justify-content-between align-items-center">
              <strong>{quiz.question}</strong>
              <span className="badge bg-secondary">
                {quiz.is_published ? (quiz.is_closed ? 'Closed' : 'Live') : 'Draft'}
              </span>
            </div>
            <ul className="mb-2">
              {quiz.options && quiz.options.map((o) => (
                <li key={o.id}>{o.option_text} {o.votes_count ? `(${o.votes_count})` : ''}</li>
              ))}
            </ul>
            <div className="d-flex gap-2">
              {!quiz.is_published && (
                <button className="btn btn-sm btn-success" onClick={() => toggleQuizPublish(quiz)}>
                  Publish
                </button>
              )}
              {quiz.is_published && !quiz.is_closed && (
                <button className="btn btn-sm btn-warning" onClick={() => closeQuiz(quiz)}>
                  Close
                </button>
              )}
              {quiz.is_published && quiz.is_closed && (
                <button className="btn btn-sm btn-success" onClick={() => reopenQuiz(quiz)}>
                  Reopen
                </button>
              )}
              <Link to={`./quiz/${quiz.id}/results`} className="btn btn-sm btn-info">
                Results
              </Link>
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={() => {
                  setQuizToClear(quiz);
                  setClearPhrase('');
                }}
              >
                Clear responses
              </button>
              <button
                className="btn btn-sm btn-outline-dark"
                title="Download QR for this quiz"
                onClick={() =>
                  downloadQr(
                    `https://quiz.velorates.org/${quiz.id}`,
                    `quiz-${quiz.id}-qr.png`
                  )
                }
              >
                QR
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={() => deleteQuiz(quiz.id)}>
                Delete
              </button>
            </div>
            <div className="mt-2">
              <img
                src={qrSrc(`https://quiz.velorates.org/${quiz.id}`)}
                alt={`Quiz ${quiz.id} QR`}
                style={{ width: '100px' }}
              />
            </div>
          </div>
        ))
      )}

      {quizToClear && (
        <>
          <div className="modal-backdrop show" />
          <div className="modal show d-block" tabIndex="-1">
            <div
              className="modal-dialog modal-dialog-centered"
              role="dialog"
              aria-modal="true"
              aria-labelledby="clear-responses-title"
            >
              <div className="modal-content">
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    clearQuizResponses();
                  }}
                >
                  <div className="modal-header border-0 pb-0">
                    <h5 className="modal-title text-danger" id="clear-responses-title">
                      <span className="me-2" aria-hidden="true">&#9888;</span>
                      Clear quiz responses?
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      disabled={clearingResponses}
                      onClick={() => {
                        setQuizToClear(null);
                        setClearPhrase('');
                      }}
                    />
                  </div>
                  <div className="modal-body">
                    <p className="text-danger fw-semibold">
                      This permanently deletes every response for “{quizToClear.question}” and resets its option counts.
                    </p>
                    <label htmlFor="clear-responses-confirmation" className="form-label">
                      Type <strong>CLEAR</strong> to continue.
                    </label>
                    <input
                      id="clear-responses-confirmation"
                      className="form-control"
                      type="text"
                      autoComplete="off"
                      value={clearPhrase}
                      onChange={(event) => setClearPhrase(event.target.value)}
                      disabled={clearingResponses}
                    />
                  </div>
                  <div className="modal-footer border-0 pt-0">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={clearingResponses}
                      onClick={() => {
                        setQuizToClear(null);
                        setClearPhrase('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-danger"
                      disabled={clearPhrase !== 'CLEAR' || clearingResponses}
                    >
                      {clearingResponses ? 'Clearing...' : 'Clear all responses'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SessionDetail;
